import React, { useEffect, useState } from "react";
import { Box, Grid, IconButton, Tooltip } from "@mui/material";
import FlexBetween from "../../components/FlexBetween";
import DataTable from "../../components/DataGrids/DataGrid";
import { GridColDef } from "@mui/x-data-grid";
import { useNavigate, useSearchParams } from "react-router-dom";
import ButtonComponent from "../../components/Button.Component";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import AddSharpIcon from "@mui/icons-material/AddSharp";
import {
  deleteExecutionSubGroup,
  reactivateExecutionSubGroup,
} from "../../services/executionSubGroup";
import useFetchPaginatedExecutionSubGroup from "../../hooks/useFetchPaginatedExecutionSubGroup";
import { getFormattedDateTime } from "../../services/utils";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import {
  IconDeleteBlueSvg,
  IconEditBase,
  IconEyeBase,
  IconCheckCircleIconGreenSvg,
  IconCrossCircleIconSvg,
  IconReactivateSvg,
} from "../../common/utils/icons";
import { useToast } from "../../services/utils";
import Loader from "../../components/Molecules/Loader/Loader";
import LimitChractersWithTooltip from "../../common/utils/LimitChractersWithTooltip";
import ConfirmDailog from "../../components/Dialogs/ConfirmDailog";
import SearchFilterBar from "../../components/Molecules/FilterSearch/SearchFilterBar";
import useDebounce from "../../hooks/useDebounce";
import { useAuth } from "../../contexts/AuthContext";
import { getConfig } from "../../config/runtimeConfig";

// Centralized Filter Configuration for Execution Sub Group List
const EXECUTION_SUB_GROUP_FILTER_CONFIG = {
  // Default filter state - single source of truth
  DEFAULT_STATE: {
    name: "",
    status_filter: "Active",
  },

  // Fields to persist in URL
  get URL_FIELDS() {
    return Object.keys(this.DEFAULT_STATE);
  },

  // Field type mappings for URL serialization/deserialization
  FIELD_TYPES: {
    ID_FIELDS: [],
    STRING_FIELDS: ["name", "status_filter"],
    DATE_FIELDS: [],
  },
} as const;

const ExecutionSubGroup: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const { hasPermission, isAdmin } = useAuth();
  const canAdd = isAdmin || hasPermission("execution_sub_group", "add");
  const canEdit = isAdmin || hasPermission("execution_sub_group", "edit");
  const canDelete = isAdmin || hasPermission("execution_sub_group", "delete");
  //for pagination default values and states
  const defaultPage = getConfig("REACT_APP_DEFAULT_PAGE", "1");
  const { rowsPerPage } = useRowsPerPage();
  const [page, setPage] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1
  );
  const [pSize, setPSize] = useState<number>(rowsPerPage);
  // states
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState<boolean>(false);
  const [fileData, setFileData] = useState<any>([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [subGroupToDelete, setSubGroupToDelete] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // SearchFilterBar state - initialize from URL parameters
  const [searchFilterData, setSearchFilterData] = useState<any>(() => {
    const initialState: any = {
      ...EXECUTION_SUB_GROUP_FILTER_CONFIG.DEFAULT_STATE,
    };

    // Restore filters from URL parameters
    EXECUTION_SUB_GROUP_FILTER_CONFIG.URL_FIELDS.forEach((field: string) => {
      const urlValue = searchParams.get(field);
      if (urlValue) {
        initialState[field] = urlValue;
      }
    });

    return initialState;
  });

  // Extract filters from searchFilterData
  const effectiveStatusFilter = searchFilterData?.status_filter || "";
  const effectiveName = searchFilterData?.name || "";

  // Debounce name with 400ms delay
  const debouncedName = useDebounce(effectiveName);

  // Determine if we should include name in API call (minimum 3 characters)
  const effectiveNameSearch =
    debouncedName.trim().length >= 3 ? debouncedName : "";

  // hooks
  const [fetchedSubGroup] = useFetchPaginatedExecutionSubGroup({
    setIsLoading: setIsDownloadLoading,
    page,
    pSize,
    searchTerm: effectiveNameSearch,
    statusFilter: effectiveStatusFilter,
  });

  // Sync filters to URL parameters (state → URL)
  useEffect(() => {
    const newParams = new URLSearchParams();

    EXECUTION_SUB_GROUP_FILTER_CONFIG.URL_FIELDS.forEach((field) => {
      const value = searchFilterData[field];
      if (value !== null && value !== undefined && value !== "") {
        // Don't add default values to URL to keep it clean
        if (field === "status_filter" && value === "Active") {
          return; // Skip default status filter
        }
        newParams.set(field, String(value));
      }
    });

    // Only update URL if params have changed
    const currentParamsString = searchParams.toString();
    const newParamsString = newParams.toString();
    if (currentParamsString !== newParamsString) {
      setSearchParams(newParams, { replace: true });
    }
  }, [searchFilterData, searchParams, setSearchParams]);

  // Update filter state when URL changes (URL → state for back/forward navigation)
  useEffect(() => {
    const urlFilterState: any = {
      ...EXECUTION_SUB_GROUP_FILTER_CONFIG.DEFAULT_STATE,
    };

    // Extract filter parameters from URL
    EXECUTION_SUB_GROUP_FILTER_CONFIG.URL_FIELDS.forEach((field: string) => {
      const urlValue = searchParams.get(field);
      if (urlValue) {
        urlFilterState[field] = urlValue;
      }
    });

    // Only update state if URL differs from current state
    const hasChanges = EXECUTION_SUB_GROUP_FILTER_CONFIG.URL_FIELDS.some(
      (field) => urlFilterState[field] !== searchFilterData[field]
    );

    if (hasChanges) {
      setSearchFilterData(urlFilterState);
    }
  }, [searchParams]);

  // Reset page to 1 when search filter changes
  useEffect(() => {
    if (
      searchFilterData?.name !== undefined ||
      searchFilterData?.status_filter !== undefined
    ) {
      setPage(1);
    }
  }, [searchFilterData]);

  const handelClickEvent = () => {
    navigate("/execution-sub-group/add");
  };

  // Handle delete execution sub group
  const handleDeleteSubGroup = (subGroup: any) => {
    setSubGroupToDelete(subGroup);
    setDeleteConfirmOpen(true);
  };

  // Handle reactivate execution sub group
  const handleReactivate = async (id: number, name: string) => {
    setIsLoading(true);
    try {
      await reactivateExecutionSubGroup(id);
      showToast(
        `Execution Sub Group "${name}" reactivated successfully`,
        "success"
      );

      // Optimistic UI update - immediately update the is_active flag in local state
      setFileData((prev: any) => ({
        ...prev,
        items: prev?.items?.map((item: any) =>
          item.id === id ? { ...item, is_active: true } : item
        ),
      }));
    } catch (error) {
      showToast("Failed to reactivate execution sub group", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Confirm delete action
  const confirmDelete = async () => {
    if (!subGroupToDelete) return;

    try {
      setDeleteLoading(true);
      const result = await deleteExecutionSubGroup(subGroupToDelete.id);
      if (result && result.status === 200) {
        showToast(`${subGroupToDelete.name} deleted successfully`, "success");
        setDeleteConfirmOpen(false);
        setSubGroupToDelete(null);
        // Update the list to remove the deleted item
        const updatedData = fileData?.items?.filter(
          (sg: { id: any }) => sg.id !== subGroupToDelete.id
        );
        setFileData((prev: any) => ({
          ...prev,
          items: updatedData,
        }));
      }
    } catch (error: any) {
      showToast(
        error?.message || "Failed to delete execution sub group",
        "error"
      );
    } finally {
      setDeleteLoading(false);
      setDeleteConfirmOpen(false);
    }
  };

  const columns: GridColDef[] = [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      width: 1,
      minWidth: 20,
      flex: 0,
      renderCell: (params: any) => <></>,
      cellClassName: "no-padding-cell",
      headerClassName: "no-padding-cell",
    },
    {
      field: "id",
      headerName: "ID",
      minWidth: 80,
      flex: 1,
      renderCell: (params: any) => {
        return <span>{params.id}</span>;
      },
    },
    {
      field: "name",
      headerName: "Name",
      minWidth: 140,
      flex: 1,
      renderCell: (params: any) => {
        return <LimitChractersWithTooltip value={params?.value} />;
      },
    },
    {
      field: "code",
      headerName: "Code",
      minWidth: 140,
      flex: 1,
      renderCell: (params: any) => {
        return <LimitChractersWithTooltip value={params?.value} />;
      },
    },
    {
      field: "description",
      headerName: "Description",
      minWidth: 180,
      flex: 1,
      renderCell: (params: any) => {
        return <LimitChractersWithTooltip value={params?.value} />;
      },
    },
    {
      field: "created_on",
      headerName: "Created On",
      minWidth: 180,
      flex: 1,
      renderCell: (params: any) => {
        return getFormattedDateTime(params.value);
      },
    },
    {
      field: "is_active",
      headerName: "Status",
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params: any) => {
        return params.value ? (
          <Tooltip title="Active" placement="top" arrow>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconCheckCircleIconGreenSvg width={21} height={21} />
            </Box>
          </Tooltip>
        ) : (
          <Tooltip title="Inactive" placement="top" arrow>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconCrossCircleIconSvg width={21} height={21} />
            </Box>
          </Tooltip>
        );
      },
    },

    {
      field: "action",
      headerName: "Actions",
      align: "center",
      headerAlign: "center",
      width: 170,
      renderCell: (params: any) => {
        const handleDelete = () => {
          handleDeleteSubGroup(params.row);
        };
        const isActive = params.row.is_active === true;

        return (
          <>
            {/* For active records, show all action buttons */}
            {isActive && (
              <>
                <Tooltip title="View Execution Sub Group" placement="top" arrow>
                  <IconButton
                    className="datagrid-action-btn"
                    color="inherit"
                    onClick={() => {
                      navigate(`/execution-sub-group/view/${params.row.id}`);
                    }}
                  >
                    <IconEyeBase />
                  </IconButton>
                </Tooltip>
                {canEdit && (
                  <Tooltip
                    title="Edit Execution Sub Group"
                    placement="top"
                    arrow
                  >
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() => {
                        navigate(`/execution-sub-group/edit/${params.row.id}`);
                      }}
                    >
                      <IconEditBase />
                    </IconButton>
                  </Tooltip>
                )}
                {canDelete && (
                  <Tooltip
                    title="Delete Execution Sub Group"
                    placement="top"
                    arrow
                  >
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() => handleDelete()}
                    >
                      <IconDeleteBlueSvg />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}

            {/* For inactive records, show Reactivate button if user can edit */}
            {!isActive && canEdit && (
              <Tooltip
                title="Reactivate Execution Sub Group"
                placement="top"
                arrow
              >
                <IconButton
                  className="datagrid-action-btn"
                  color="inherit"
                  onClick={() =>
                    handleReactivate(params.row.id, params.row.name)
                  }
                >
                  <IconReactivateSvg />
                </IconButton>
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  // for update Execution Sub Group list data
  useEffect(() => {
    if (fetchedSubGroup) {
      setFileData(fetchedSubGroup);
    }
  }, [fetchedSubGroup]);

  return (
    <Box>
      {isDownloadLoading && <Loader isLoading={isDownloadLoading} />}
      {deleteLoading && <Loader isLoading={deleteLoading} zIndex={1400} />}
      <ConfirmDailog
        isDailogOpen={deleteConfirmOpen}
        handleCancelAction={() => setDeleteConfirmOpen(false)}
        handleConfirmAction={confirmDelete}
        dailogTitle="Delete Execution Sub Group"
        dailogDescription={`Are you sure you want to delete execution sub group "${subGroupToDelete?.name}"?`}
      />
      <Box className="text-box-card list-page-card compact-text-box-card">
        <Grid container rowSpacing={2.5} columnSpacing={2}>
          <Grid item xs>
            <SearchFilterBar
              largeWidth={true}
              setSearchFilterData={setSearchFilterData}
              searchFilterData={searchFilterData}
              requiredFields={["name", "status_filter"]}
              primaryFields={["name", "status_filter"]}
              fieldRequirements={{
                namePlaceholder: "Search by name",
                defaultStatusFilter: "Active", // Reset to Active status on Clear Filter
              }}
            />
          </Grid>
          {canAdd && (
            <Grid
              item
              xs="auto"
              sx={{ display: "flex", alignItems: "flex-start" }}
            >
              <ButtonComponent
                handelClickEvent={handelClickEvent}
                className="btn-orange"
              >
                <AddSharpIcon sx={{ marginRight: "4px" }} /> Execution Sub Group
              </ButtonComponent>
            </Grid>
          )}
        </Grid>
      </Box>

      <FlexBetween>
        <DataTable
          dataColumns={columns}
          dataRows={fileData?.items || []}
          loading={isLoading}
          dataListTitle={"Execution Sub Group List"}
          className="dataTable no-radius hide-progress-icon"
          paginationMode="server"
          rowCount={fileData?.total}
          paginationModel={{
            page: page - 1,
            pageSize: pSize,
          }}
          onPaginationModelChange={(params: any) => {
            if (params.pageSize !== pSize || params.page !== page - 1) {
              setPage(params.page + 1);
              setPSize(params.pageSize);
            }
          }}
          singlePageMaxHeightDiff={368}
        />
      </FlexBetween>
    </Box>
  );
};

export default ExecutionSubGroup;
